import { convertToMinutes, extractHHMM } from "./utils";

/**
 * Shared parent/child service-chain scheduling for Manual Time Change and Drag & Drop.
 *
 * IMPORTANT: Call the same scheduleServiceMove for both interactions, but pass
 * interactionMode: "time" (default) for dropdowns and "drag" for drag-and-drop.
 * Client matrices differ when a card is dragged past its children.
 *
 * Inferred business rules (from client result matrices):
 * - Time slot and Drag share most rules but differ when a card is dragged past its children.
 * - Services form linked chains in booking order; initially fully linked.
 * - Link topology uses scheduleLinkParentKey (not array index). Array order may
 *   change after moves; do not treat "first in array" as the only chain root.
 * - Move first (chain root) via TIME: keep links; shift all still-linked descendants by delta.
 * - Move first (or any sub-chain root) via DRAG past its next linked child: detach alone;
 *   children stay put; the former direct child becomes the new sub-chain root.
 * - Move non-first to a time still at/after the previous service: break link to previous;
 *   moved service becomes root of its sub-chain; linked descendants follow by delta
 *   (unless the drag-past-children rule applies).
 * - Column/therapist/room-only change (same absolute start, delta 0): keep links; no cascade.
 *   Callers apply therapist/room on the moved card only (matches dropdown behavior).
 * - Move non-first to a time BEFORE the previous service: EXTRACT — moved service leaves
 *   alone; descendants splice onto the previous service; gap closes.
 * - Links never auto re-establish for an extracted / detached service.
 * - Split segments are not chain nodes; they follow their parent base by the same delta.
 */

export const isScheduleChainService = (service) =>
	!!(service && !service.isSplitSegment);

export const getServiceAbsoluteStartMinutes = (service) => {
	if (typeof service?.absoluteMinutes === "number" && !Number.isNaN(service.absoluteMinutes)) {
		return service.absoluteMinutes;
	}
	const dayOffset =
		typeof service?.dayOffset === "number" && !Number.isNaN(service.dayOffset)
			? service.dayOffset
			: 0;
	const start = extractHHMM(service?.start_time || "");
	if (!start) return dayOffset * 1440;
	return dayOffset * 1440 + convertToMinutes(start);
};

/**
 * Start used only for "do these two cards meet?".
 * absoluteMinutes stays the schedule source, including overnight (-45 for 23:15).
 * When that value's clock does not match the displayed start_time, use the
 * displayed clock on the same day offset so a 2:00 / 3:10 gap is not hidden.
 */
const getChainAdjacencyStartMinutes = (service) => {
	const abs = getServiceAbsoluteStartMinutes(service);
	const clock = extractHHMM(service?.start_time || "");
	if (!clock || Number.isNaN(abs)) return abs;
	const clockMinutes = convertToMinutes(clock);
	if (Number.isNaN(clockMinutes)) return abs;
	const absClock = ((abs % 1440) + 1440) % 1440;
	if (absClock === clockMinutes) return abs;
	return Math.floor(abs / 1440) * 1440 + clockMinutes;
};

/** True when the next card starts at the exact minute the previous one ends. */
export const areServicesBackToBack = (previousService, nextService) => {
	if (!previousService || !nextService) return false;
	const previousStart = getChainAdjacencyStartMinutes(previousService);
	const nextStart = getChainAdjacencyStartMinutes(nextService);
	const duration = Number(previousService?.duration);
	if (Number.isNaN(previousStart) || Number.isNaN(nextStart)) return false;
	const previousEnd =
		Number.isFinite(duration) && duration > 0 ? previousStart + duration : previousStart;
	return nextStart === previousEnd;
};

const asBrokenChainLink = (service) => ({
	...service,
	linkedToPrevious: false,
	scheduleLinkParentKey: null,
	scheduleLinkBroken: true,
});

/**
 * Edit load: a pax pair whose start does not meet the previous end is already broken.
 * scheduleLinkBroken stays set so a later edit that makes the times touch does not re-link them.
 * Back-to-back pairs are left untouched.
 */
export const markMismatchedEditChainLinksBroken = (services = []) => {
	const list = Array.isArray(services) ? services : [];
	return list.map((svc, index) => {
		if (!isScheduleChainService(svc)) return { ...svc };
		if (svc.scheduleLinkBroken === true) return asBrokenChainLink(svc);
		const prevIdx = findPreviousChainIndex(list, index);
		if (prevIdx < 0) return svc;
		if (areServicesBackToBack(list[prevIdx], svc)) return svc;
		return asBrokenChainLink(svc);
	});
};

/**
 * Sort by true absolute timeline and re-seed parent links.
 * Fixes day-2 card order where early-morning child (00:45) was listed before
 * overnight parent (23:15 → abs -45) and wrongly became the chain root.
 *
 * @param {object} [options]
 * @param {boolean} [options.forceRelink] - When true, consecutive abs-ordered
 *   services are re-linked (API/card order repair). Still honors preserveUnlinkKeys.
 * @param {Set<string>} [options.preserveUnlinkKeys] - Item keys that must stay roots.
 */
export const sortAndRelinkChainByAbsoluteMinutes = (
	services = [],
	resolveKey = resolveServiceScheduleKey,
	options = {}
) => {
	const forceRelink = options.forceRelink === true;
	const preserveUnlinkKeys =
		options.preserveUnlinkKeys instanceof Set ? options.preserveUnlinkKeys : null;

	const list = Array.isArray(services) ? services.map((svc) => ({ ...svc })) : [];
	if (list.length <= 1) {
		return ensureServiceLinkedFlags(list, resolveKey);
	}

	list.sort(
		(a, b) => getServiceAbsoluteStartMinutes(a) - getServiceAbsoluteStartMinutes(b)
	);

	const reseated = list.map((svc, index) => {
		const key = resolveKey(svc);
		const keyStr = key != null ? String(key) : null;
		const preserveExplicitRoot =
			keyStr != null && preserveUnlinkKeys != null && preserveUnlinkKeys.has(keyStr);

		if (index === 0) {
			return {
				...svc,
				linkedToPrevious: false,
				scheduleLinkParentKey: null,
			};
		}

		if (
			preserveExplicitRoot ||
			svc.scheduleLinkBroken === true ||
			(!forceRelink && svc.linkedToPrevious === false)
		) {
			return svc.scheduleLinkBroken === true
				? asBrokenChainLink(svc)
				: {
					...svc,
					linkedToPrevious: false,
					scheduleLinkParentKey: null,
				};
		}

		if (!forceRelink) {
			// Sort only — keep existing link topology (intentional breaks / parent keys).
			return { ...svc };
		}

		// Abs-order repair must not invent a chain across a gap or overlap.
		// A pair that does not meet is already broken; scheduleLinkBroken keeps it
		// from sticking again if a later edit makes the times touch.
		const prevIdx = findPreviousChainIndex(list, index);
		if (prevIdx >= 0 && !areServicesBackToBack(list[prevIdx], svc)) {
			return asBrokenChainLink(svc);
		}

		return {
			...svc,
			linkedToPrevious: true,
			// Clear stale keys so ensureServiceLinkedFlags parents from abs order
			scheduleLinkParentKey: null,
		};
	});

	return ensureServiceLinkedFlags(reseated, resolveKey);
};

/** True when a linked child starts before its parent on the abs timeline. */
export const chainHasInvertedParentChildOrder = (
	services = [],
	resolveKey = resolveServiceScheduleKey
) => {
	const list = Array.isArray(services) ? services : [];
	for (let i = 0; i < list.length; i += 1) {
		if (!isScheduleChainService(list[i])) continue;
		const parentKey = getScheduleLinkParentKey(list, i, resolveKey);
		if (parentKey == null) continue;
		const parentIdx = findServiceIndexByKey(list, parentKey, resolveKey);
		if (parentIdx < 0) continue;
		if (
			getServiceAbsoluteStartMinutes(list[i]) <
			getServiceAbsoluteStartMinutes(list[parentIdx])
		) {
			return true;
		}
	}
	return false;
};

export const absoluteMinutesToClock = (absoluteMinutes) => {
	const normalized = Number(absoluteMinutes);
	if (Number.isNaN(normalized)) {
		return { start_time: "00:00", dayOffset: 0, absoluteMinutes: 0 };
	}
	const dayOffset = Math.floor(normalized / 1440);
	const clock = ((normalized % 1440) + 1440) % 1440;
	const hh = Math.floor(clock / 60);
	const mm = clock % 60;
	return {
		start_time: `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`,
		dayOffset,
		absoluteMinutes: normalized,
	};
};

export const resolveServiceScheduleKey = (service) => {
	if (service == null) return null;
	// Prefer stable booking item ids over UI counterKey. Live edit services use
	// counterKey 1/2/3 while booking_item_id is 8037… — linking by counterKey
	// breaks cascade when the move path resolves by booking_item_id.
	if (service.booking_item_id != null) return service.booking_item_id;
	if (service.item_id != null) return service.item_id;
	if (service.counterKey != null && service.counterKey !== "") return service.counterKey;
	if (service.id != null) return service.id;
	return null;
};

export const findServiceIndexByKey = (services, movedKey, resolveKey = resolveServiceScheduleKey) => {
	if (movedKey == null) return -1;
	return (services || []).findIndex((svc) => String(resolveKey(svc)) === String(movedKey));
};

/** 0-based position among chain (non-split) services only. */
export const getChainPosition = (services, originalIndex) => {
	let pos = 0;
	for (let i = 0; i < originalIndex; i += 1) {
		if (isScheduleChainService(services[i])) pos += 1;
	}
	return pos;
};

export const findPreviousChainIndex = (services, fromIndex) => {
	for (let i = fromIndex - 1; i >= 0; i -= 1) {
		if (isScheduleChainService(services[i])) return i;
	}
	return -1;
};

/**
 * Stable parent key for a chain service. Prefers scheduleLinkParentKey so links survive
 * counterKey / display re-ordering (e.g. after extract when UI sorts by counterKey).
 */
export const getScheduleLinkParentKey = (
	services,
	index,
	resolveKey = resolveServiceScheduleKey
) => {
	const svc = services?.[index];
	if (!isScheduleChainService(svc)) return null;
	if (svc.linkedToPrevious === false) return null;
	if (svc.scheduleLinkParentKey != null && svc.scheduleLinkParentKey !== "") {
		return String(svc.scheduleLinkParentKey);
	}
	const prevIdx = findPreviousChainIndex(services, index);
	if (prevIdx < 0) return null;
	const key = resolveKey(services[prevIdx]);
	return key != null ? String(key) : null;
};

/**
 * Indices of the root service plus all services whose link-parent chain leads to it.
 * Uses scheduleLinkParentKey so Body→5in1 stays intact even if Foot sits between them
 * in array order after a counterKey sort.
 */
export const getLinkedDescendantIndices = (
	services,
	rootOriginalIndex,
	resolveKey = resolveServiceScheduleKey
) => {
	if (!Array.isArray(services) || rootOriginalIndex < 0) return [];
	const rootKey = resolveKey(services[rootOriginalIndex]);
	if (rootKey == null) return [rootOriginalIndex];

	const indices = [rootOriginalIndex];
	const included = new Set([rootOriginalIndex]);
	let grew = true;
	while (grew) {
		grew = false;
		for (let i = 0; i < services.length; i += 1) {
			if (included.has(i) || !isScheduleChainService(services[i])) continue;
			const parentKey = getScheduleLinkParentKey(services, i, resolveKey);
			if (parentKey == null) continue;
			const parentIdx = findServiceIndexByKey(services, parentKey, resolveKey);
			if (parentIdx >= 0 && included.has(parentIdx)) {
				indices.push(i);
				included.add(i);
				grew = true;
			}
		}
	}
	indices.sort((a, b) => a - b);
	return indices;
};

/**
 * Ensure linkedToPrevious is explicit on chain services.
 *
 * IMPORTANT: Do NOT force "first in array" to be unlinked. After time/drag moves,
 * display or override rebuilds can reorder by clock time so a child appears before
 * its parent. Wiping that child's scheduleLinkParentKey breaks cascade on the next
 * root move (only the dragged card moves). Trust explicit link flags / parent keys.
 *
 * - Explicit unlink (linkedToPrevious === false) stays a root.
 * - Existing scheduleLinkParentKey is preserved when still linked.
 * - Otherwise seed parent from the previous chain item in array order (initial load).
 */
export const ensureServiceLinkedFlags = (services = [], resolveKey = resolveServiceScheduleKey) => {
	const list = Array.isArray(services) ? services : [];
	return list.map((svc, index) => {
		if (!isScheduleChainService(svc)) {
			return { ...svc };
		}
		if (svc.scheduleLinkBroken === true) {
			return asBrokenChainLink(svc);
		}
		if (svc.linkedToPrevious === false) {
			return { ...svc, linkedToPrevious: false, scheduleLinkParentKey: null };
		}
		if (svc.scheduleLinkParentKey != null && svc.scheduleLinkParentKey !== "") {
			return {
				...svc,
				linkedToPrevious: true,
				scheduleLinkParentKey: String(svc.scheduleLinkParentKey),
			};
		}
		const prevIdx = findPreviousChainIndex(list, index);
		const parentKey = prevIdx >= 0 ? resolveKey(list[prevIdx]) : null;
		if (parentKey == null) {
			return { ...svc, linkedToPrevious: false, scheduleLinkParentKey: null };
		}
		// Seed a parent only when the cards actually meet. An explicit
		// linkedToPrevious:true (create / in-session chain) is left alone.
		// A missing flag — edit rows reloaded without chain state — is a
		// permanent break so the first drag does not move both cards.
		if (!areServicesBackToBack(list[prevIdx], svc)) {
			if (svc.linkedToPrevious === true) {
				return {
					...svc,
					linkedToPrevious: true,
					scheduleLinkParentKey: String(parentKey),
				};
			}
			return asBrokenChainLink(svc);
		}
		return {
			...svc,
			linkedToPrevious: true,
			scheduleLinkParentKey: String(parentKey),
		};
	});
};

const applyAbsoluteScheduleToService = (service, nextAbsoluteStart) => {
	const duration = Number(service?.duration) || 0;
	const startClock = absoluteMinutesToClock(nextAbsoluteStart);
	const endClock = absoluteMinutesToClock(nextAbsoluteStart + duration);
	const updated = {
		...service,
		start_time: startClock.start_time,
		end_time: endClock.start_time,
		dayOffset: startClock.dayOffset,
		absoluteMinutes: startClock.absoluteMinutes,
	};
	if (Array.isArray(service?.room_segments) && service.room_segments.length > 0) {
		updated.room_segments = service.room_segments.map((segment) => ({
			...segment,
			start_time: startClock.start_time,
			end_time: endClock.start_time,
			duration: duration || segment?.duration,
		}));
	}
	return updated;
};

const shiftSplitSegmentsForBases = (services, baseKeys, deltaMinutes) => {
	if (!deltaMinutes || !baseKeys?.size) return services;
	return services.map((svc) => {
		if (!svc?.isSplitSegment) return svc;
		const parentKey = svc.splitParent != null ? String(svc.splitParent) : null;
		if (!parentKey || !baseKeys.has(parentKey)) return svc;
		const prevAbs = getServiceAbsoluteStartMinutes(svc);
		return applyAbsoluteScheduleToService(svc, prevAbs + deltaMinutes);
	});
};

/**
 * Extract a non-first service that jumped before its previous neighbour:
 * - moved service becomes independent at newAbs
 * - its former linked descendants splice onto the previous service
 * - gap closes by packing the first descendant at previousEnd
 * - array reorders to [...leftChainIncludingSplicedDescs, ...others, extracted]
 */
const extractServiceBeforePrevious = ({
	services,
	movedIndex,
	previousIndex,
	newAbs,
	resolveKey,
}) => {
	const working = services.map((svc) => ({ ...svc }));
	// Descendants while still linked through moved (before we break/extract)
	const linkedThroughMoved = getLinkedDescendantIndices(working, movedIndex, resolveKey);
	const descendantIndices = linkedThroughMoved.filter((idx) => idx !== movedIndex);

	const previous = working[previousIndex];
	const previousKey = resolveKey(previous);
	const prevAbs = getServiceAbsoluteStartMinutes(previous);
	const prevEnd = prevAbs + (Number(previous.duration) || 0);

	const movedKey = resolveKey(working[movedIndex]);
	const descendantKeys = descendantIndices.map((idx) => String(resolveKey(working[idx])));

	// Apply extract time to moved; always unlinked from previous
	let extracted = applyAbsoluteScheduleToService(
		{
			...working[movedIndex],
			linkedToPrevious: false,
			scheduleLinkParentKey: null,
		},
		newAbs
	);

	// Close gap for descendants onto previous end; re-parent first descendant onto previous
	let closeDelta = 0;
	const updatedDescendants = descendantIndices.map((idx, order) => {
		const svc = working[idx];
		const oldAbs = getServiceAbsoluteStartMinutes(svc);
		if (order === 0) {
			closeDelta = prevEnd - oldAbs;
		}
		const nextAbs = oldAbs + closeDelta;
		return applyAbsoluteScheduleToService(
			{
				...svc,
				linkedToPrevious: true,
				scheduleLinkParentKey:
					order === 0
						? previousKey != null
							? String(previousKey)
							: null
						: svc.scheduleLinkParentKey != null
							? String(svc.scheduleLinkParentKey)
							: String(resolveKey(working[descendantIndices[order - 1]])),
			},
			nextAbs
		);
	});

	// Rebuild order: everything except moved+descendants, then previous stays in place
	// with descendants immediately after previous, extracted at end (independent).
	const skipKeys = new Set(
		[String(movedKey), ...descendantKeys].filter(Boolean)
	);
	const head = [];
	for (let i = 0; i < working.length; i += 1) {
		const svc = working[i];
		if (!isScheduleChainService(svc)) {
			// Keep split segments with their relative place for now; shifted below
			const key = resolveKey(svc);
			if (key != null && skipKeys.has(String(key))) continue;
			head.push(svc);
			continue;
		}
		const key = resolveKey(svc);
		if (key != null && skipKeys.has(String(key))) continue;
		head.push(svc);
		if (i === previousIndex) {
			updatedDescendants.forEach((d) => head.push(d));
		}
	}

	// If previous was not visited (shouldn't happen), append descendants before extracted
	const headKeys = new Set(
		head.map((s) => resolveKey(s)).filter((k) => k != null).map(String)
	);
	updatedDescendants.forEach((d) => {
		const k = resolveKey(d);
		if (k != null && !headKeys.has(String(k))) {
			head.push(d);
			headKeys.add(String(k));
		}
	});

	head.push(extracted);

	const extractDelta = newAbs - getServiceAbsoluteStartMinutes(services[movedIndex]);
	let next = ensureServiceLinkedFlags(head, resolveKey);
	const extractedKeySet = new Set(
		movedKey != null && movedKey !== "" ? [String(movedKey)] : []
	);
	next = shiftSplitSegmentsForBases(next, extractedKeySet, extractDelta);
	const descBaseKeys = new Set(descendantKeys);
	next = shiftSplitSegmentsForBases(next, descBaseKeys, closeDelta);

	const newMovedIndex = findServiceIndexByKey(next, movedKey, resolveKey);
	const affectedIndices = [];
	next.forEach((svc, idx) => {
		const key = resolveKey(svc);
		if (key != null && skipKeys.has(String(key))) {
			affectedIndices.push(idx);
		}
	});

	return {
		services: next,
		affectedIndices,
		deltaMinutes: extractDelta,
		movedIndex: newMovedIndex,
		mode: "extract",
	};
};

/**
 * Drag-only: moved service jumps to a start strictly after its next linked child.
 * Move only the dragged card; leave children in place; promote direct children to roots.
 */
const detachMovedLeaveDescendants = ({
	services,
	movedIndex,
	newAbs,
	resolveKey,
}) => {
	const working = services.map((svc) => ({ ...svc }));
	const movedKey = resolveKey(working[movedIndex]);
	const movedKeyStr = movedKey != null ? String(movedKey) : null;
	const oldAbs = getServiceAbsoluteStartMinutes(working[movedIndex]);

	const descendantIndices = getLinkedDescendantIndices(working, movedIndex, resolveKey).filter(
		(idx) => idx !== movedIndex
	);

	descendantIndices.forEach((idx) => {
		const parentKey = getScheduleLinkParentKey(working, idx, resolveKey);
		if (movedKeyStr != null && parentKey === movedKeyStr) {
			working[idx] = {
				...working[idx],
				linkedToPrevious: false,
				scheduleLinkParentKey: null,
			};
		}
	});

	working[movedIndex] = applyAbsoluteScheduleToService(
		{
			...working[movedIndex],
			linkedToPrevious: false,
			scheduleLinkParentKey: null,
		},
		newAbs
	);

	const next = ensureServiceLinkedFlags(working, resolveKey);
	const newMovedIndex = findServiceIndexByKey(next, movedKey, resolveKey);
	// Only the moved card changes time. Left-behind children only lose the link —
	// include them so callers can persist linkedToPrevious:false, but they must
	// not restamp overnight abs/service_at (Home skips schedule write if abs unchanged).
	const affectedKeys = new Set(
		[movedKeyStr, ...descendantIndices.map((idx) => resolveKey(services[idx]))]
			.filter((k) => k != null && k !== "")
			.map(String)
	);
	const affectedIndices = [];
	next.forEach((svc, idx) => {
		const key = resolveKey(svc);
		if (key != null && affectedKeys.has(String(key))) {
			affectedIndices.push(idx);
		}
	});

	return {
		services: next,
		affectedIndices,
		deltaMinutes: newAbs - oldAbs,
		movedIndex: newMovedIndex,
		mode: "drag-detach-past-children",
	};
};

const findEarliestDirectChildIndex = (services, movedIndex, resolveKey) => {
	const movedKey = resolveKey(services[movedIndex]);
	if (movedKey == null) return -1;
	const movedKeyStr = String(movedKey);
	let bestIdx = -1;
	let bestAbs = Infinity;
	for (let i = 0; i < services.length; i += 1) {
		if (i === movedIndex || !isScheduleChainService(services[i])) continue;
		const parentKey = getScheduleLinkParentKey(services, i, resolveKey);
		if (parentKey !== movedKeyStr) continue;
		const abs = getServiceAbsoluteStartMinutes(services[i]);
		if (abs < bestAbs) {
			bestAbs = abs;
			bestIdx = i;
		}
	}
	return bestIdx;
};

/**
 * Single scheduling algorithm for manual time change and drag-and-drop.
 *
 * @param {Object} params
 * @param {Array} params.services - one pax/main group
 * @param {string|number} params.movedKey - counterKey / booking_item_id of moved service
 * @param {number} params.newAbsoluteStartMinutes - new absolute start for the moved service
 * @param {'time'|'drag'} [params.interactionMode='time'] - time dropdown vs drag/drop
 * @param {Function} [params.resolveKey]
 * @returns {{ services: Array, affectedIndices: number[], deltaMinutes: number, movedIndex: number, mode?: string }}
 */
export const scheduleServiceMove = ({
	services,
	movedKey,
	newAbsoluteStartMinutes,
	interactionMode = "time",
	resolveKey = resolveServiceScheduleKey,
}) => {
	if (!Array.isArray(services) || services.length === 0) {
		return { services: services || [], affectedIndices: [], deltaMinutes: 0, movedIndex: -1 };
	}

	let next = ensureServiceLinkedFlags(services, resolveKey);
	// Overnight / day-2 card order often lists the morning child before the parent.
	// Force abs-order relink only when topology is inverted so intentional breaks stay intact.
	next = sortAndRelinkChainByAbsoluteMinutes(next, resolveKey, {
		forceRelink: chainHasInvertedParentChildOrder(next, resolveKey),
	});
	const movedIndex = findServiceIndexByKey(next, movedKey, resolveKey);
	if (movedIndex < 0 || !isScheduleChainService(next[movedIndex])) {
		return { services: next, affectedIndices: [], deltaMinutes: 0, movedIndex: -1 };
	}

	const newAbs = Number(newAbsoluteStartMinutes);
	if (Number.isNaN(newAbs)) {
		return { services: next, affectedIndices: [], deltaMinutes: 0, movedIndex };
	}

	const oldAbs = getServiceAbsoluteStartMinutes(next[movedIndex]);
	const deltaMinutes = newAbs - oldAbs;
	// Root = no link-parent. Prefer topology over array index so cascade still works
	// when children were reordered ahead of the parent in the services array.
	const isMovedRoot = getScheduleLinkParentKey(next, movedIndex, resolveKey) == null;
	const isDrag = interactionMode === "drag";

	// Therapist/room column change with no time change: keep chain links (dropdown parity).
	// Caller still applies column fields on the moved card via affectedIndices.
	if (deltaMinutes === 0) {
		return {
			services: next,
			affectedIndices: [movedIndex],
			deltaMinutes: 0,
			movedIndex,
			mode: "column-only",
		};
	}

	// Drag-only: jumping past the next linked child detaches the moved card alone.
	// Only when the child is actually AFTER the parent on the timeline — inverted
	// overnight abs (childAbs < oldAbs) is data/order corruption, not a real past-child move.
	if (isDrag) {
		const firstChildIdx = findEarliestDirectChildIndex(next, movedIndex, resolveKey);
		if (firstChildIdx >= 0) {
			const firstChildAbs = getServiceAbsoluteStartMinutes(next[firstChildIdx]);
			if (firstChildAbs > oldAbs && newAbs > firstChildAbs) {
				return detachMovedLeaveDescendants({
					services: next,
					movedIndex,
					newAbs,
					resolveKey,
				});
			}
		}
	}

	// Non-root + jumped before its link-parent → extract + close gap (client Time step 3+)
	if (!isMovedRoot) {
		const parentKey = getScheduleLinkParentKey(next, movedIndex, resolveKey);
		const previousIndex =
			parentKey != null
				? findServiceIndexByKey(next, parentKey, resolveKey)
				: findPreviousChainIndex(next, movedIndex);

		if (previousIndex >= 0 && parentKey != null) {
			const previousAbs = getServiceAbsoluteStartMinutes(next[previousIndex]);
			if (newAbs < previousAbs) {
				return extractServiceBeforePrevious({
					services: next,
					movedIndex,
					previousIndex,
					newAbs,
					resolveKey,
				});
			}
		}

		// Standard Rule 3: break from previous; take linked descendants with you
		next[movedIndex] = {
			...next[movedIndex],
			linkedToPrevious: false,
			scheduleLinkParentKey: null,
		};
	}

	// Rule 2–4: moved service + still-linked descendants shift by the same delta
	const affectedIndices = getLinkedDescendantIndices(next, movedIndex, resolveKey);
	const affectedBaseKeys = new Set();

	affectedIndices.forEach((idx) => {
		const svc = next[idx];
		const prevAbs = getServiceAbsoluteStartMinutes(svc);
		const nextAbs = idx === movedIndex ? newAbs : prevAbs + deltaMinutes;
		next[idx] = applyAbsoluteScheduleToService(svc, nextAbs);
		if (svc?.counterKey != null) {
			affectedBaseKeys.add(String(svc.counterKey));
		}
		const key = resolveKey(svc);
		if (key != null) affectedBaseKeys.add(String(key));
	});

	next = shiftSplitSegmentsForBases(next, affectedBaseKeys, deltaMinutes);

	return {
		services: next,
		affectedIndices,
		deltaMinutes,
		movedIndex,
		mode: isMovedRoot ? "cascade-root" : "break-and-follow",
	};
};

/**
 * Build a lightweight chain-service list from API booking items (one customer/pax group),
 * merging any existing drag overrides (including linkedToPrevious).
 *
 * @param {Array} items
 * @param {Object} overridesByItemId
 * @param {Function} [resolveAbsoluteMinutes] - (item, override) => number
 */
export const buildChainServicesFromBookingItems = (items = [], overridesByItemId = {}, resolveAbsoluteMinutes = null) => {
	const list = Array.isArray(items) ? items : [];
	const preserveUnlinkKeys = new Set();
	const built = list.map((item, chainIndex) => {
		const itemId = item?.id ?? item?.booking_item_id;
		const override =
			itemId != null
				? overridesByItemId[itemId] || overridesByItemId[String(itemId)] || null
				: null;

		const rawStart =
			override?.start ||
			(typeof item?.service_at === "string"
				? (item.service_at.split(" ")[1] || "").slice(0, 5)
				: "") ||
			extractHHMM(item?.start_time || "") ||
			"";

		const duration = Number(override?.duration ?? item?.totalDuration ?? item?.duration) || 0;
		let absoluteMinutes =
			typeof override?.absoluteStartMinutes === "number" && !Number.isNaN(override.absoluteStartMinutes)
				? override.absoluteStartMinutes
				: null;

		if (absoluteMinutes == null && typeof resolveAbsoluteMinutes === "function") {
			const resolved = resolveAbsoluteMinutes(item, override);
			if (typeof resolved === "number" && !Number.isNaN(resolved)) {
				absoluteMinutes = resolved;
			}
		}

		if (absoluteMinutes == null && rawStart) {
			absoluteMinutes = convertToMinutes(rawStart);
		}

		const linkedToPrevious =
			override?.linkedToPrevious === false
				? false
				: override?.linkedToPrevious === true
					? true
					: chainIndex > 0;
		if (override?.linkedToPrevious === false && itemId != null) {
			preserveUnlinkKeys.add(String(itemId));
		}
		const scheduleLinkParentKey =
			linkedToPrevious === false
				? null
				: override?.scheduleLinkParentKey != null && override.scheduleLinkParentKey !== ""
					? String(override.scheduleLinkParentKey)
					: null;

		const endAbs =
			absoluteMinutes != null ? absoluteMinutes + duration : null;
		const endClock =
			endAbs != null
				? absoluteMinutesToClock(endAbs)
				: { start_time: extractHHMM(item?.end_time || "") };

		return {
			booking_item_id: itemId,
			item_id: itemId,
			counterKey: itemId,
			duration,
			start_time: rawStart,
			end_time: override?.end || endClock.start_time,
			absoluteMinutes: absoluteMinutes ?? 0,
			dayOffset:
				typeof absoluteMinutes === "number"
					? Math.floor(absoluteMinutes / 1440)
					: 0,
			linkedToPrevious,
			scheduleLinkParentKey,
			therapist: item?.therapist_id ?? item?.therapist ?? null,
			therapist_name: item?.therapist ?? item?.therapist_name ?? "",
			name: item?.service_name || item?.name || "",
			service_name: item?.service_name || item?.name || "",
		};
	});

	// Force abs-order links so overnight 23:15 (-45) is root before 00:45 (45).
	// Card/API order often lists the morning segment first.
	return sortAndRelinkChainByAbsoluteMinutes(built, resolveServiceScheduleKey, {
		forceRelink: true,
		preserveUnlinkKeys,
	});
};

/**
 * Find the customer/pax booking_item array that contains the given item id.
 */
export const findBookingItemGroupForItemId = (booking, itemId) => {
	if (!booking?.booking_item || itemId == null) return null;
	const entries = Object.entries(booking.booking_item);
	for (const [, items] of entries) {
		if (!Array.isArray(items)) continue;
		if (items.some((item) => String(item?.id ?? item?.booking_item_id) === String(itemId))) {
			return items;
		}
	}
	return null;
};
